import { useEffect, useRef, useState } from "react";

import { Button } from "../../ui/Button";
import { cn } from "../../ui/cn";
import { describeColor, hexToHsv, hsvToHex, type Hsv } from "./palette";

/**
 * Pick an exact colour: a saturation/value field, a hue rail, opacity, and the
 * value as text.
 *
 * Hand-built rather than pulled in. `apps/web` depends on `clsx` and nothing
 * else in this area, and a colour picker library would be the largest thing on
 * a page whose job is drawing charts. What is actually needed is two draggable
 * rectangles and some arithmetic that already lives in `palette.ts`.
 *
 * Not `<input type="color">`, which would be a one-liner: it opens the
 * operating system's picker, which on macOS is a separate floating window that
 * steals focus from the popover and closes it, has no opacity in any consistent
 * way, and looks nothing like the rest of Pulse. The brand-alignment case the
 * PRD asks for — paste in an exact hex — is also awkward there and trivial here.
 */
export function ColorPicker({
  value,
  onChange,
}: {
  /** Any hex the rest of the system stores, with or without alpha. */
  value: string;
  onChange: (hex: string) => void;
}) {
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(value) ?? FALLBACK);
  /*
   * What is in the text field, which is not always a colour yet: somebody
   * typing "#d8" has not finished. Held separately so the field does not fight
   * the person using it, and only pushed outward when it parses.
   */
  const [typed, setTyped] = useState(value);

  // Follows the value when it changes underneath — switching series in the
  // panel reuses this component rather than remounting it.
  useEffect(() => {
    const parsed = hexToHsv(value);
    if (parsed) setHsv(parsed);
    setTyped(value);
  }, [value]);

  const apply = (next: Hsv): void => {
    setHsv(next);
    const hex = hsvToHex(next);
    setTyped(hex);
    onChange(hex);
  };

  const opaque = hsvToHex({ ...hsv, a: 1 });
  const written = describeColor(hsv);

  return (
    <div className="flex w-56 flex-col gap-2">
      <SaturationField hsv={hsv} onChange={apply} />

      <Rail
        label="Hue"
        value={hsv.h / 360}
        onChange={(ratio) => apply({ ...hsv, h: ratio * 360 })}
        background="linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)"
      />

      <Rail
        label="Opacity"
        value={hsv.a}
        onChange={(ratio) => apply({ ...hsv, a: ratio })}
        background={`linear-gradient(to right, transparent, ${opaque})`}
        checkered
      />

      <div className="flex items-center gap-1.5">
        <span
          aria-hidden
          className="size-6 shrink-0 rounded border border-line"
          style={{ background: CHECKERBOARD }}
        >
          <span
            className="block size-full rounded"
            style={{ backgroundColor: hsvToHex(hsv) }}
          />
        </span>
        <input
          value={typed}
          onChange={(event) => {
            const next = event.target.value;
            setTyped(next);
            const parsed = hexToHsv(next);
            // Only when it is a colour. Pushing every keystroke outward would
            // repaint the chart black halfway through typing "#d8227a".
            if (parsed) {
              setHsv(parsed);
              onChange(hsvToHex(parsed));
            }
          }}
          spellCheck={false}
          aria-label="Colour as hex"
          className={cn(
            "h-7 min-w-0 flex-1 rounded-md border bg-app px-1.5 font-mono text-xs text-content",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
            hexToHsv(typed) ? "border-line" : "border-sla-breached",
          )}
        />
      </div>

      {/* The other two notations, to copy rather than to edit. A brand guide
          quotes whichever one it happens to quote, and retyping an rgb() into
          hex by hand is exactly the busywork this is meant to remove. */}
      <div className="flex gap-1">
        {([["RGB", written.rgb], ["HSL", written.hsl]] as const).map(
          ([name, text]) => (
            <Button
              key={name}
              size="sm"
              variant="secondary"
              className="min-w-0 flex-1 font-mono !text-[10px]"
              title={`Copy ${text}`}
              onClick={() => void navigator.clipboard?.writeText(text)}
            >
              <span className="truncate">{text}</span>
            </Button>
          ),
        )}
      </div>
    </div>
  );
}

const FALLBACK: Hsv = { h: 331, s: 0.84, v: 0.85, a: 1 };

/** The grey chequer behind a translucent swatch, so "50%" is visibly 50%. */
const CHECKERBOARD =
  "repeating-conic-gradient(#0002 0% 25%, transparent 0% 50%) 50% / 8px 8px";

/**
 * The saturation/value square, dragged with a pointer or nudged with arrows.
 *
 * Pointer capture rather than window listeners: the drag has to keep working
 * when the cursor leaves the square, and capture means the browser routes the
 * events back here without this component having to add and remove global
 * handlers it might fail to clean up.
 */
function SaturationField({
  hsv,
  onChange,
}: {
  hsv: Hsv;
  onChange: (hsv: Hsv) => void;
}) {
  const field = useRef<HTMLDivElement>(null);

  const pick = (event: React.PointerEvent<HTMLDivElement>): void => {
    const box = field.current?.getBoundingClientRect();
    if (!box) return;

    onChange({
      ...hsv,
      s: clamp01((event.clientX - box.left) / box.width),
      v: 1 - clamp01((event.clientY - box.top) / box.height),
    });
  };

  return (
    <div
      ref={field}
      role="slider"
      tabIndex={0}
      aria-label="Saturation and brightness"
      aria-valuetext={`${Math.round(hsv.s * 100)}% saturation, ${Math.round(hsv.v * 100)}% brightness`}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        pick(event);
      }}
      onPointerMove={(event) => {
        if (event.buttons === 1) pick(event);
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 0.1 : 0.02;
        const moves: Record<string, Partial<Hsv>> = {
          ArrowLeft: { s: clamp01(hsv.s - step) },
          ArrowRight: { s: clamp01(hsv.s + step) },
          ArrowDown: { v: clamp01(hsv.v - step) },
          ArrowUp: { v: clamp01(hsv.v + step) },
        };
        const move = moves[event.key];
        if (!move) return;
        event.preventDefault();
        onChange({ ...hsv, ...move });
      }}
      className={cn(
        "relative h-28 w-full cursor-crosshair rounded-md border border-line",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
      )}
      style={{
        backgroundColor: `hsl(${hsv.h} 100% 50%)`,
        backgroundImage:
          "linear-gradient(to right, #fff, transparent), linear-gradient(to top, #000, transparent)",
      }}
    >
      <span
        aria-hidden
        className="pointer-events-none absolute size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
        style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
      />
    </div>
  );
}

/** A horizontal slider over a gradient — hue, or opacity. */
function Rail({
  label,
  value,
  onChange,
  background,
  checkered,
}: {
  label: string;
  value: number;
  onChange: (ratio: number) => void;
  background: string;
  checkered?: boolean;
}) {
  const rail = useRef<HTMLDivElement>(null);

  const pick = (event: React.PointerEvent<HTMLDivElement>): void => {
    const box = rail.current?.getBoundingClientRect();
    if (!box) return;
    onChange(clamp01((event.clientX - box.left) / box.width));
  };

  return (
    <div
      ref={rail}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      onPointerDown={(event) => {
        event.currentTarget.setPointerCapture(event.pointerId);
        pick(event);
      }}
      onPointerMove={(event) => {
        if (event.buttons === 1) pick(event);
      }}
      onKeyDown={(event) => {
        const step = event.shiftKey ? 0.1 : 0.02;
        if (event.key === "ArrowLeft") onChange(clamp01(value - step));
        else if (event.key === "ArrowRight") onChange(clamp01(value + step));
        else return;
        event.preventDefault();
      }}
      className={cn(
        "relative h-3 w-full cursor-pointer rounded-full border border-line",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
      )}
      style={{ background: checkered ? CHECKERBOARD : undefined }}
    >
      <span
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{ background }}
      />
      <span
        aria-hidden
        className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
        style={{ left: `${value * 100}%` }}
      />
    </div>
  );
}

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
